import { createMemo, createResource, onCleanup, type JSX, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLocation } from "@solidjs/router"
import { createSimpleContext } from "@opencode-ai/ui/context"
import { useLanguage } from "@/context/language"
import { datoxAgentMode, datoxV2Design, getCurrentUser, listCompanies, setDatoxAuthTokenProvider } from "@/datox/api"
import { LeshaBootSkeleton } from "@/datox/v2/page-skeleton"
import { applyLeshaHead } from "@/datox/v2/brand-head"
import { LeshaSignIn } from "@/datox/v2/sign-in"
import { clearReads, setReader } from "@/datox/v2/read-cache"
import { activeCompanyID, localDevHeaders, setActiveCompanyID } from "@/datox/local-auth"
import { DatoxContentLoading } from "@/components/datox-content-loading"
export { activeCompanyID, localDevHeaders, setActiveCompanyID } from "@/datox/local-auth"

type DatoxAuthState = {
  initialized: boolean
  accountName: string
  accountEmail: string
  accessToken: string
  error: string
}

const defaultTenantID = "95c228f4-944c-49ba-abcd-a00985e5497c"
const defaultScope = "api://d76b434a-b9ac-4bb1-b417-40d2eb65d624/user_impersonation"

export const { provider: DatoxAuthProvider, use: useDatoxAuth } = createSimpleContext({
  name: "DatoxAuth",
  gate: false,
  init: () => {
    const [state, setState] = createStore<DatoxAuthState>({
      initialized: false,
      accountName: "",
      accountEmail: "",
      accessToken: "",
      error: "",
    })

    const [client] = createResource(async () => {
      if (!isMsalConfigured()) {
        // The V2 read cache keeps each person's lists apart (read-cache.ts).
        setReader(localDevHeaders()["X-User-Id"])
        setState("initialized", true)
        setState("error", msalRequired() ? "datox.login.msalMissing" : "")
        return null
      }
      try {
        const msal = await import("@azure/msal-browser")
        const instance = new msal.PublicClientApplication({
          auth: {
            clientId: import.meta.env.VITE_DATOX_MSAL_CLIENT_ID || "",
            authority: `https://login.microsoftonline.com/${import.meta.env.VITE_DATOX_MSAL_TENANT_ID || defaultTenantID}`,
            redirectUri: import.meta.env.VITE_DATOX_MSAL_REDIRECT_URI || window.location.origin,
          },
          cache: {
            // Report previews open in independent tabs. MSAL's durable cache
            // must be shared; redirect/interaction state remains tab-local.
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false,
          },
        })
        await instance.initialize()
        const redirect = await instance.handleRedirectPromise()
        if (redirect?.account) instance.setActiveAccount(redirect.account)
        if (!instance.getActiveAccount() && instance.getAllAccounts()[0]) instance.setActiveAccount(instance.getAllAccounts()[0])
        setState("initialized", true)
        await acquireToken(instance)
        return instance
      } catch (error) {
        setState("initialized", true)
        setState("error", error instanceof Error ? error.message : String(error))
        return null
      }
    })

    const login = async () => {
      const instance = client()
      if (!instance) return
      await instance.loginRedirect({ scopes: [scope()], redirectUri: redirectUri(), state: window.location.href })
    }

    const logout = async () => {
      const instance = client()
      if (!instance) return
      clearReads()
      await instance.logoutRedirect()
    }

    const token = async (forceRefresh = false) => {
      const instance = client()
      if (!instance) return ""
      return acquireToken(instance, forceRefresh)
    }

    const configured = createMemo(isMsalConfigured)
    const authenticated = createMemo(() => !msalRequired() || !!state.accessToken)

    return {
      state,
      configured,
      authenticated,
      initializing: () => client.loading,
      login,
      logout,
      token,
    }

    async function acquireToken(instance: import("@azure/msal-browser").PublicClientApplication, forceRefresh = false) {
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0]
      if (!account) {
        setReader("")
        setState({ initialized: true, accountName: "", accountEmail: "", accessToken: "" })
        return ""
      }
      try {
        const response = await instance.acquireTokenSilent({ scopes: [scope()], account, forceRefresh })
        setReader(response.account?.homeAccountId || account.homeAccountId)
        setState({
          initialized: true,
          accountName: response.account?.name || account.name || "",
          accountEmail: response.account?.username || account.username || "",
          accessToken: response.accessToken,
          error: "",
        })
        return response.accessToken
      } catch (error) {
        setState({ accessToken: "", error: error instanceof Error ? error.message : String(error) })
        return ""
      }
    }
  },
})

export function DatoxAuthGate(props: { children: JSX.Element }) {
  const auth = useDatoxAuth()
  const language = useLanguage()
  const location = useLocation()
  setDatoxAuthTokenProvider(auth.token)
  const scoped = () => datoxAgentMode() && msalRequired() && !location.pathname.startsWith("/review/")
  const [companyAccess, companyActions] = createResource(
    () => (scoped() && auth.authenticated() ? auth.state.accountEmail || true : undefined),
    () => listCompanies().then(async (available) => {
      if (available.length) {
        if (!available.some((company) => company.id === activeCompanyID())) setActiveCompanyID(available[0].id)
        return "ready"
      }
      return getCurrentUser().then((user) => user.is_datox_admin ? "ready" : "denied").catch(() => "denied")
    }).catch(() => "error"),
  )
  const syncCompany = (event: StorageEvent) => {
    if (event.key === "activeCompanyId" && event.oldValue !== event.newValue) window.location.assign("/")
  }
  window.addEventListener("storage", syncCompany)
  onCleanup(() => window.removeEventListener("storage", syncCompany))

  // Lesha Bank (V2): the bank's tab icon, name and title from the first frame, the sign-in page included. The V2 marker on
  // <html> scopes the design tokens there (datox/v2/tokens.css), so portals get them and, in dark, Bobur's palette takes
  // the V2 ramp; the flag-off app never has it.
  if (datoxV2Design()) {
    applyLeshaHead(document, language.t("datox.v2.title.app"))
    document.documentElement.setAttribute("data-dx-v2", "")
  }

  if (!datoxAgentMode() || location.pathname.startsWith("/review/")) return props.children

  // Lesha Bank (V2): silent sign-in shows the reports outline under the bank's name, not a full-screen spinner (P21).
  const loading = () => datoxV2Design()
    ? <LeshaBootSkeleton label={language.t("datox.login.loading")} title={language.t("datox.v2.title.app")} />
    : <DatoxContentLoading title={language.t("datox.login.loading")} />

  return (
    <Show when={!auth.initializing()} fallback={loading()}>
      <Show when={auth.authenticated()} fallback={<DatoxLoginPage />}>
        <Show when={!scoped() || !companyAccess.loading} fallback={loading()}>
          <Show when={!scoped() || companyAccess() === "ready"} fallback={
            <div class="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-v2-text-text-base">
              <p role="alert">{language.t(companyAccess() === "error" ? "datox.admin.loadFailed" : "datox.company.noAccess")}</p>
              <button type="button" onClick={() => void companyActions.refetch()}>{language.t("datox.members.retry")}</button>
              <button type="button" onClick={() => void auth.logout()}>{language.t("datox.profile.logout")}</button>
            </div>
          }>
            {props.children}
          </Show>
        </Show>
      </Show>
    </Show>
  )

  function DatoxLoginPage() {
    if (datoxV2Design())
      return (
        <LeshaSignIn
          logoAlt={language.t("datox.logo.alt")}
          title={language.t("datox.login.title")}
          subtitle={language.t("datox.login.panel.description")}
          button={auth.initializing() ? language.t("datox.login.loading") : language.t("datox.login.button")}
          footer={language.t("datox.login.footer")}
          error={auth.state.error === "datox.login.msalMissing" ? language.t("datox.login.msalMissing") : auth.state.error || undefined}
          busy={auth.initializing()}
          disabled={auth.initializing() || !auth.configured()}
          onLogin={() => void auth.login()}
        />
      )
    return (
      <div class="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[#06131a] px-6 py-8 text-white">
        <div class="pointer-events-none absolute -left-24 top-10 h-80 w-80 rounded-full bg-[#0085ff]/20 blur-3xl" />
        <div class="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-[#34d399]/10 blur-3xl" />
        <div class="relative flex w-full max-w-xl flex-col items-center rounded-[28px] border border-white/10 bg-white/[0.04] px-8 py-12 text-center shadow-2xl backdrop-blur md:px-14 md:py-16">
          <img src="/datox-logo-white.svg" alt={language.t("datox.logo.alt")} class="h-16 w-auto md:h-20" />
          <div class="mt-16 inline-flex rounded-full border border-[#0085ff]/35 bg-[#0085ff]/10 px-5 py-2 text-18-medium text-[#7cc4ff]">
            {language.t("datox.login.badge")}
          </div>
          <Show when={auth.state.error}>
            <div class="mt-8 w-full rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-13-regular text-red-100">
              {auth.state.error === "datox.login.msalMissing" ? language.t("datox.login.msalMissing") : auth.state.error}
            </div>
          </Show>
          <button
            class="mt-8 flex h-12 w-full max-w-sm items-center justify-center rounded-xl bg-[#0085ff] px-4 text-14-medium text-white shadow-lg shadow-[#0085ff]/25 transition hover:bg-[#1d93ff] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={auth.initializing() || !auth.configured()}
            onClick={() => void auth.login()}
          >
            {auth.initializing() ? language.t("datox.login.loading") : language.t("datox.login.button")}
          </button>
        </div>
      </div>
    )
  }
}

function isMsalConfigured() {
  return !!import.meta.env.VITE_DATOX_MSAL_CLIENT_ID
}

function redirectUri() {
  return import.meta.env.VITE_DATOX_MSAL_REDIRECT_URI || window.location.origin
}

function msalRequired() {
  if (!datoxAgentMode()) return false
  if (isMsalConfigured()) return true
  try {
    const url = new URL(import.meta.env.VITE_DATOX_AGENT_API_URL || "http://localhost:8088")
    return url.hostname !== "localhost" && url.hostname !== "127.0.0.1"
  } catch {
    return true
  }
}

function scope() {
  return import.meta.env.VITE_DATOX_MSAL_SCOPE || defaultScope
}
